import React, { useState } from 'react';
import {
  Container,
  Typography,
  TextField,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Paper,
  Slider,
} from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function App() {
  const [initialInvestment, setInitialInvestment] = useState('');
  const [monthlyContribution, setMonthlyContribution] = useState('');
  const [timeHorizon, setTimeHorizon] = useState('');
  const [currency, setCurrency] = useState('KES');
  const [mmfRate, setMmfRate] = useState('13.6');
  const [bankRate, setBankRate] = useState('3');
  const [compoundingFrequency, setCompoundingFrequency] = useState('daily');

  const calculateReturns = () => {
    const initial = parseFloat(initialInvestment) || 0;
    const monthly = parseFloat(monthlyContribution) || 0;
    const years = parseFloat(timeHorizon) || 0;
    const mmfRateDecimal = parseFloat(mmfRate) / 100;
    const bankRateDecimal = parseFloat(bankRate) / 100;
    const daysInYear = 365;
    const monthsInYear = 12;

    let mmfBalance = initial;
    let bankBalance = initial;
    const mmfData = [initial];
    const bankData = [initial];
    const totalContribution = initial + (monthly * years * 12);

    // Calculate month by month
    for (let month = 1; month <= years * 12; month++) {
      if (compoundingFrequency === 'daily') {
        // For MMF with daily compounding
        const daysInMonth = 30; // Using 30 days for consistency
        for (let day = 1; day <= daysInMonth; day++) {
          mmfBalance = mmfBalance * (1 + (mmfRateDecimal / daysInYear));
        }
      } else {
        // For MMF with monthly compounding
        mmfBalance = mmfBalance * (1 + mmfRateDecimal / 12);
      }
      
      // Add monthly contribution after interest calculation
      mmfBalance += monthly;

      // For Bank: Always use simple monthly compounding
      bankBalance = bankBalance * (1 + bankRateDecimal / 12) + monthly;

      mmfData.push(mmfBalance);
      bankData.push(bankBalance);
    }

    return { 
      mmfData, 
      bankData, 
      totalContribution
    };
  };

  const { mmfData, bankData, totalContribution } = calculateReturns();

  const chartData = {
    labels: Array.from({ length: timeHorizon * 12 + 1 }, (_, i) => i === 0 ? 'Start' : 'Month ' + i),
    datasets: [
      {
        label: 'MMF Investment',
        data: mmfData,
        borderColor: '#f47f36',
        backgroundColor: '#f47f36',
      },
      {
        label: 'Bank Savings',
        data: bankData,
        borderColor: '#06315e',
        backgroundColor: '#06315e',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 1.5,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          boxWidth: 10,
          padding: 10,
          font: {
            size: 11
          }
        }
      }
    },
    scales: {
      y: {
        ticks: {
          callback: (value) => `${currency} ${value.toLocaleString()}`,
          font: {
            size: 11
          }
        }
      },
      x: {
        ticks: {
          font: {
            size: 11
          }
        }
      }
    }
  };

  const currencySymbol = currency === 'USD' ? '$' : 'KES';

  return (
    <Container maxWidth="md" sx={{ py: 2, fontFamily: 'Poppins' }}>
      <Typography 
        variant="h5" 
        component="h1" 
        gutterBottom 
        sx={{ 
          color: '#06315e',
          fontFamily: 'Poppins',
          fontWeight: 600,
          textAlign: 'center',
          mb: 2
        }}
      >
        Money Market Fund Calculator
      </Typography>

      <Typography 
        variant="subtitle1" 
        sx={{ 
          mb: 2, 
          color: '#06315e',
          fontFamily: 'Poppins',
          fontWeight: 500,
          textAlign: 'center'
        }}
      >
        Compare your potential returns between a money market fund and traditional bank savings
      </Typography>

      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: '300px 1fr' }, 
        gap: 2,
      }}>
        <Box component={Paper} elevation={3} sx={{ p: 2 }}>
          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <FormLabel sx={{ fontFamily: 'Poppins', color: '#06315e' }}>Select Currency</FormLabel>
            <RadioGroup
              row
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            >
              <FormControlLabel 
                value="KES" 
                control={<Radio sx={{ color: '#f47f36', '&.Mui-checked': { color: '#f47f36' } }} />} 
                label="KES" 
              />
              <FormControlLabel 
                value="USD" 
                control={<Radio sx={{ color: '#f47f36', '&.Mui-checked': { color: '#f47f36' } }} />} 
                label="USD" 
              />
            </RadioGroup>
          </FormControl>

          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <FormLabel sx={{ fontFamily: 'Poppins', color: '#06315e' }}>Compounding Frequency</FormLabel>
            <RadioGroup
              row
              value={compoundingFrequency}
              onChange={(e) => setCompoundingFrequency(e.target.value)}
            >
              <FormControlLabel 
                value="daily" 
                control={<Radio sx={{ color: '#f47f36', '&.Mui-checked': { color: '#f47f36' } }} />} 
                label="Daily" 
              />
              <FormControlLabel 
                value="monthly" 
                control={<Radio sx={{ color: '#f47f36', '&.Mui-checked': { color: '#f47f36' } }} />} 
                label="Monthly" 
              />
            </RadioGroup>
          </FormControl>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Initial Investment"
              value={initialInvestment}
              onChange={(e) => setInitialInvestment(e.target.value)}
              fullWidth
              type="number"
              InputProps={{
                startAdornment: <span style={{ marginRight: 8 }}>{currencySymbol}</span>,
              }}
              sx={{ mb: 2 }}
            />

            <TextField
              label="Monthly Contribution"
              value={monthlyContribution}
              onChange={(e) => setMonthlyContribution(e.target.value)}
              fullWidth
              type="number"
              InputProps={{
                startAdornment: <span style={{ marginRight: 8 }}>{currencySymbol}</span>,
              }}
              sx={{ mb: 2 }}
            />

            <TextField
              label="Investment Period (Years)"
              value={timeHorizon}
              onChange={(e) => setTimeHorizon(e.target.value)}
              fullWidth
              type="number"
              sx={{ mb: 2 }}
            />

            <TextField
              label="MMF Rate (%)"
              value={mmfRate}
              onChange={(e) => setMmfRate(e.target.value)}
              type="number"
              fullWidth
              sx={{ mb: 2 }}
            />

            <TextField
              label="Bank Rate (%)"
              value={bankRate}
              onChange={(e) => setBankRate(e.target.value)}
              type="number"
              fullWidth
              sx={{ mb: 2 }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box component={Paper} elevation={3} sx={{ p: 2 }}>
            <Typography 
              variant="h5" 
              sx={{ 
                mb: 2, 
                color: '#06315e',
                fontFamily: 'Poppins',
                fontWeight: 600,
                textAlign: 'center'
              }}
            >
              {`Projected Returns After ${timeHorizon || 0} ${timeHorizon === '1' ? 'Year' : 'Years'}`}
            </Typography>

            <Line options={options} data={chartData} />
          </Box>

          <Box component={Paper} elevation={3} sx={{ p: 2 }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                mb: 2,
                fontFamily: 'Poppins',
                fontWeight: 600,
                color: '#06315e',
                textAlign: 'center'
              }}
            >
              Summary
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography 
                sx={{ 
                  fontFamily: 'Poppins',
                  fontWeight: 500,
                  color: '#666',
                  textAlign: 'center',
                  p: 2,
                  bgcolor: '#f5f5f5',
                  borderRadius: 1
                }}
              >
                Total Contribution: {currency} {totalContribution?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                <Typography 
                  sx={{ 
                    flex: 1,
                    fontFamily: 'Poppins',
                    fontWeight: 500,
                    color: '#f47f36',
                    textAlign: 'center',
                    p: 2,
                    bgcolor: '#fff5ef',
                    borderRadius: 1
                  }}
                >
                  MMF Investment: {currency} {mmfData[mmfData.length - 1]?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </Typography>

                <Typography 
                  sx={{ 
                    flex: 1,
                    fontFamily: 'Poppins',
                    fontWeight: 500,
                    color: '#06315e',
                    textAlign: 'center',
                    p: 2,
                    bgcolor: '#f0f5fa',
                    borderRadius: 1
                  }}
                >
                  Bank Savings: {currency} {bankData[bankData.length - 1]?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '600px', margin: '0 auto' }}>
          This calculator is for informational purposes only and does not constitute financial advice. Actual returns may vary based on market conditions and other factors. Past performance is not indicative of future results.
        </Typography>
      </Box>
    </Container>
  );
}

export default App;
